import { useRuntimeConfig } from '#imports'
import * as Sentry from '@sentry/nuxt'
import { isChunkLoadError } from './lib/chunkLoadError'
import { isExpectedClientNoise, sentryEventTexts } from './lib/sentryClientNoise'

// Static-token direct sign-in links carry their secret in the URL fragment
// (never sent to the server), but Sentry's browser SDK still reads
// window.location.href client-side for breadcrumbs/event context, which would
// otherwise leak the token into Sentry. Strip the fragment on that one route.
const STATIC_TOKEN_LOGIN_PATH = /\/[^/]+\/t(?:[/?#]|$)/

function scrubStaticTokenUrl (url: string | undefined | null): string | undefined {
  if (!url) {
    return url ?? undefined
  }
  if (!STATIC_TOKEN_LOGIN_PATH.test(url)) {
    return url
  }
  const hashIndex = url.indexOf('#')
  return hashIndex === -1 ? url : url.slice(0, hashIndex) + '#[redacted]'
}

if (useRuntimeConfig().public.sentry.dsn && useRuntimeConfig().public.sentry.disabled !== 'true') {
  Sentry.init({
    debug: useRuntimeConfig().public.sentry.debug === 'true',
    dsn: useRuntimeConfig().public.sentry.dsn,
    environment: useRuntimeConfig().public.sentry.environment,
    beforeBreadcrumb (breadcrumb) {
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = scrubStaticTokenUrl(breadcrumb.data.url)
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = scrubStaticTokenUrl(breadcrumb.data.to)
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = scrubStaticTokenUrl(breadcrumb.data.from)
      }
      return breadcrumb
    },
    ignoreErrors: [
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
      /Importing a module script failed/i,
      /Unable to preload CSS/i,
      /(^|:\s*)Network Error$/i,
      /ResizeObserver loop/i,
      /Ignored attempt to cancel a touch/i,
      /The play method is not allowed by the user agent/i,
      /play\(\) failed because the user didn't interact/i,
      /Non-Error promise rejection captured with value:\s*undefined/i
    ],
    beforeSend (event) {
      if (isExpectedClientNoise(event)) {
        if (sentryEventTexts(event).some(text => isChunkLoadError(text))) {
          event.fingerprint = ['chunk-load-error']
        }
        return null
      }
      if (event.request?.url) {
        event.request.url = scrubStaticTokenUrl(event.request.url)
      }
      if (event.transaction) {
        event.transaction = scrubStaticTokenUrl(event.transaction)
      }
      return event
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration()
    ],
    tracesSampler: (samplingContext) => {
      const { name } = samplingContext
      if (name.includes('tervarumpu') || name.includes('marko') || name.includes('shopid')) {
        return 1.0
      }
      return 0.0
    },
    profilesSampler: (samplingContext) => {
      const { name } = samplingContext
      if (name.includes('tervarumpu') || name.includes('marko') || name.includes('shopid')) {
        return 1.0
      }
      return 0.0
    },
  })
}